<template>
    <Layout>
        <v-container v-if="$store.getters['user/model']">
            <h1 v-bind:class="$style.heading">
                Update Profile
            </h1>
            <h3 v-bind:class="$style.subheading" class="font-weight-thin gray subtitle-3">
                Your unique generated username:
                <span class="font-weight-light font-condensed font-italic">
                    {{ $store.getters['user/model'].username }}
                </span>
            </h3>
            <VAlert
                v-if="Object.keys(serverErrors).length"
                v-bind:value="serverErrors"
                v-bind:class="$style.alert"
                type="warning"
            >
                <h3>Please fix the following errors</h3>
                <ul>
                    <li
                        v-for="(serverError, index) in serverErrors"
                        v-bind:key="`error-${index}`"
                    >
                        {{ serverError.message }}
                    </li>
                </ul>
            </VAlert>

            <VAlert
                v-bind:value="success"
                v-bind:class="$style.alert"
                type="success"
            >
                <h3>Your profile has been updated!</h3>
            </VAlert>
            <v-form v-model="valid">
                <v-container pa-0>
                    <v-layout v-bind:class="$style.form">
                        <VFlex
                            xs12
                            md6
                        >
                            <v-text-field
                                v-model="alias"
                                v-bind:counter="30"
                                label="Alias"
                                required
                            />
                        </VFlex>
                        <VFlex
                            xs12
                            md6
                        >
                            <v-text-field
                                v-model="email"
                                label="E-mail"
                                required
                            />
                        </VFlex>
                        <VBtn
                            large
                            depressed
                            color="primary"
                            v-bind:disabled="isSubmitting"
                            v-bind:loading="isSubmitting"
                            v-on:click="updateProfile"
                        >
                            Update
                        </VBtn>
                    </v-layout>
                </v-container>
            </v-form>
            <br>
            <h3 v-bind:class="$style.avatarHeading">
                Update Avatar
            </h3>
            <v-layout>
                <VFlex
                    v-if="avatar"
                    xs2
                    mr-4
                >
                    <v-img
                        v-if="avatar"
                        v-bind:src="avatar"
                        lazy-src
                        aspect-ratio="1"
                        class="grey lighten-2"
                        max-height="10rem"
                        max-width="10rem"
                    />
                </VFlex>
                <VFlex>
                    <FilePond
                        ref="pond"
                        v-bind:class="$style.dropArea"
                        name="profile_pic"
                        label-idle="Drop profile image here..."
                        allow-multiple="false"
                        accepted-file-types="image/jpeg, image/png"
                        v-bind:server="serverConfiguration"
                        v-bind:files="files"
                    />
                </VFlex>
            </v-layout>
            <h3 v-bind:class="$style.avatarHeading">
                Email Notification Settings
            </h3>
            <template>
                <VRadioGroup v-model="notificationFrequency" v-on:change="updateNotificationSetting">
                    <VRadio
                        v-for="(radio, index) in notificationRadios"
                        v-bind:key="`radio-${index}`"
                        v-bind:label="radio.label"
                        v-bind:value="radio.value"
                    />
                </VRadioGroup>
            </template>
            <h3 v-bind:class="$style.avatarHeading">
                Update Password
            </h3>
            <template>
                <VFlex>
                    <form>
                        <v-text-field
                            v-model="oldPassword"
                            v-bind:type="passwordInputType"
                            label="Old Password"
                            v-bind:error-messages="
                                serverErrors.oldPassword ||
                                    TextFieldValidation_getErrorMessages('oldPassword')
                            "
                            required
                            v-on:blur="$v.oldPassword.$touch()"
                            v-on:keypress.enter="updatePassword"
                        />
                        <v-text-field
                            v-model="password"
                            v-bind:type="passwordInputType"
                            label="New Password"
                            v-bind:error-messages="
                                serverErrors.password ||
                                    TextFieldValidation_getErrorMessages('password', {
                                        messages: {
                                            hasLowercaseCharacter:
                                                'Password must contain at least one lower case letter.',
                                            hasUppercaseCharacter:
                                                'Password must contain at least one upper case letter.',
                                            hasSpecialCharacter:
                                                'Password must contain at least one number or symbol.',
                                        },
                                    })
                            "
                            required
                            v-on:blur="$v.password.$touch()"
                            v-on:keypress.enter="updatePassword"
                        />
                        <v-text-field
                            v-model="passwordConfirmation"
                            v-bind:type="passwordInputType"
                            label="Confirm New Password"
                            v-bind:error-messages="
                                serverErrors.passwordConfirmation ||
                                    TextFieldValidation_getErrorMessages('passwordConfirmation', {
                                        messages: {
                                            hasLowercaseCharacter:
                                                'Password must contain at least one lower case letter.',
                                            hasUppercaseCharacter:
                                                'Password must contain at least one upper case letter.',
                                            hasSpecialCharacter:
                                                'Password must contain at least one number or symbol.',
                                        },
                                    })
                            "
                            required
                            v-on:blur="$v.passwordConfirmation.$touch()"
                            v-on:keypress.enter="updatePassword"
                        />
                        <VCheckbox v-model="isShowingPassword" label="Show password" hide-details />
                        <br>
                        <VBtn
                            large
                            depressed
                            color="primary"
                            v-bind:disabled="isSubmitting"
                            v-bind:loading="isSubmitting"
                            v-on:click="updatePassword"
                        >
                            Update Password
                        </VBtn>
                    </form>
                </VFlex>
            </template>
        </v-container>
    </Layout>
</template>

<script>
    import Layout from '@layouts/main';
    import vueFilePond from 'vue-filepond';
    import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
    import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
    import {
        API_BASE_URL,
    } from '@constants/api';
    import { TOKEN_NAME } from '@constants/token';
    import { setTimeout } from 'timers';
    import _has from 'lodash/has';
    import TextFieldValidation from '@mixins/TextFieldValidation';
    import {
        required, minLength,
    } from 'vuelidate/lib/validators';

    const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginImagePreview);

    export default {

        /**
         * Declaratively register required components.
         *
         * @link https://vuejs.org/v2/guide/components.html#Local-Registration
         */
        components: {
            Layout,
            FilePond,
        },

        /**
         * Mixins are a flexible way to distribute reusable functionalities for Vue components.
         * A mixin object can contain any component options. When a component uses a mixin,
         * all options in the mixin will be “mixed” into the component’s own options.
         *
         * @link https://vuejs.org/v2/guide/mixins.html
         */
        mixins: [
            TextFieldValidation,
        ],

        /**
         * You must define defaults for all Vue models and reactive Vue template data.
         *
         * @link https://vuejs.org/v2/api/#data
         *
         * @return Object Vue model and binding defaults.
         */
        data () {
            return {
                files: [],
                alias: null,
                email: null,
                avatar: null,
                valid: true,
                isSubmitting: false,
                serverConfiguration: {},
                serverErrors: {},
                error: null,
                success: false,
                notificationFrequency: 60,
                password: '',
                oldPassword: '',
                passwordConfirmation: '',
                isShowingPassword: false,
                notificationRadios: [
                    {
                        value: 1440,
                        label: 'Daily',
                    },
                    {
                        value: 60,
                        label: 'Hourly',
                    },
                    {
                        value: 0,
                        label: 'No Notifications',
                    },
                ],
            };
        },

        /**
         * Custom page meta data property for the view components.
         *
         * @type {Object}
         */
        metaInfo: {
            title: 'Citizen J.',
            meta: [
                {
                    name: 'description',
                    content: 'Citizen J.',
                },
            ],
            link: [
                {
                    rel: 'canonical',
                    href: '/',
                },
            ],
        },

        /**
         * Cached computed methods for complex logic on the view layer.
         *
         * @link https://vuejs.org/v2/guide/computed.html
         *
         * @type Object The computed functions
         */
        computed: {
            passwordInputType () {
                return this.isShowingPassword ? 'text' : 'password';
            },
        },

        async created () {
            try {
                await this.$store.dispatch('user/fetch');
                this.avatar = this.$store.getters['user/model'].image;
                this.email = this.$store.getters['user/model'].email;
                this.alias = this.$store.getters['user/model'].alias;
                this.notificationFrequency = this.$store.getters['user/model'].notificationFrequency;
                await this.setServerConfiguration();
            } catch (error) {
                this.error = error;
            }
        },

        methods: {
            blurInputs () {
                [...document.querySelectorAll('form input')].forEach(element => element.blur());
            },

            setServerConfiguration () {
                const token = window.localStorage.getItem(TOKEN_NAME);

                this.serverConfiguration = {
                    url: `${API_BASE_URL}/profile/image`,
                    withCredentials: false,
                    process: {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        onload: this.fileUploaded,
                    },
                };
            },

            flashSuccess () {
                this.success = true;
                setTimeout(() => this.success = false, 3000);
            },

            updateProfile () {
                this.isSubmitting = true;
                this.axios
                    .post('/profile', {
                        email: this.email,
                        alias: this.alias,
                    })
                    .then(response => {
                        this.serverErrors = {};

                        // Determine if any field server validation errors are set
                        if (_has(response, 'data.errors')) {
                            return (this.serverErrors = response.data.errors);
                        }

                        this.$store.dispatch('user/fetch');
                        this.flashSuccess();
                    })
                    .catch(({ response }) => {
                        this.serverErrors = response.data || {};
                        this.success = null;
                    })
                    .then(() => this.isSubmitting = false);
            },

            async updatePassword () {
                // Reset server errors
                this.serverErrors = {};

                // Verify that the form is valid
                if (this.$v.$invalid) {
                    return this.$v.$touch();
                }

                this.isSubmitting = true;

                const {
                    passwordConfirmation, password, oldPassword,
                } = this;

                await this.axios
                    .post('auth/update-password', {
                        password: password,
                        password_confirmation: passwordConfirmation,
                        old_password: oldPassword,
                    })
                    .then(() => {
                        this.serverErrors = {};
                        this.flashSuccess();
                        this.isSubmitting = false;
                    })
                    .catch(({ response }) => {
                        this.serverErrors = response.data || {};
                        this.success = null;
                    })
                    .then(() => this.isSubmitting = false);

                this.password = this.passwordConfirmation = this.oldPassword = '';
                this.blurInputs();
                this.$v.$reset();
            },

            updateNotificationSetting () {
                this.isSubmitting = true;
                this.axios
                    .post('/profile/notifications', {
                        frequency: this.notificationFrequency,
                    })
                    .then(() => {
                        this.serverErrors = null;
                        this.$store.dispatch('user/fetch');
                        this.flashSuccess();
                    })
                    .catch(({ response }) => {
                        this.serverErrors = response.data || {};
                        this.success = null;
                    })
                    .then(() => this.isSubmitting = false);
            },
            fileUploaded (uniqueFileId, load, error) {
                this.avatar = uniqueFileId;
            },
        },

        /**
         * Define custom validation rules on reactive data for Vuelidate validation.
         *
         * @link https://monterail.github.io/vuelidate
         *
         * @type {Object}
         */
        validations: {
            oldPassword: {
                required,
            },
            password: {
                required,
                minLength: minLength(8),
                hasLowercaseCharacter: value => Boolean(value.match(/[a-z]/)),
                hasUppercaseCharacter: value => Boolean(value.match(/[A-Z]/)),
                hasSpecialCharacter: value => Boolean(value.match(/[^a-zA-Z]/)),
            },
            passwordConfirmation: {
                required,
                minLength: minLength(8),
                hasLowercaseCharacter: value => Boolean(value.match(/[a-z]/)),
                hasUppercaseCharacter: value => Boolean(value.match(/[A-Z]/)),
                hasSpecialCharacter: value => Boolean(value.match(/[^a-zA-Z]/)),
            },
        },
    };
</script>

<style lang="scss">
    @import './../../../node_modules/filepond/dist/filepond.min.css';
    @import './../../../node_modules/filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';
</style>

<style lang="scss" module>
    .form {
        @apply flex flex-col;

        @screen md {
            @apply flex-row;
        }
    }

    .heading {
        @apply mb-0;
    }

    .subheading {
        @apply mb-8;
    }

    .alert {
        @apply mb-8;
    }

    .avatarHeading {
        @apply mb-4;
    }

    .dropArea {
        @apply cursor-pointer;
    }
</style>
